import { htmlToText } from './markdown'
import {
  joinSiteUrl,
  markdownUrlForRoute,
  publicDocumentationReferences,
  routeArtifactId,
} from './routing'
import { escapeXml } from './text'
import type {
  DocumentationAssistantChunk,
  DocumentationNavigationNode,
  DocumentationPage,
  DocumentationSearchEntry,
} from './types'

export function pageMarkdown(page: DocumentationPage, basePath = '/docs'): string {
  const title = `# ${page.title}`
  const body = publicDocumentationReferences(page.markdown, basePath).trim()
  return `${title}\n${page.description ? `\n${page.description}\n` : ''}${body ? `\n${body}\n` : '\n'}`
}

export function createSearchEntries(pages: DocumentationPage[]): DocumentationSearchEntry[] {
  return pages
    .filter((page) => !page.hidden)
    .map((page) => ({
      route: page.route,
      title: page.title,
      ...(page.description ? { description: page.description } : {}),
      headings: page.headings.filter((heading) => heading.depth > 1),
      text: page.text.slice(0, 12_000),
    }))
}

export function createAssistantChunks(pages: DocumentationPage[]): DocumentationAssistantChunk[] {
  return pages.flatMap((page) => {
    if (page.hidden) return []
    const chunks: DocumentationAssistantChunk[] = []
    let index = 0
    const sections = page.html.split(/(?=<h[23]\b)/)
    for (const section of sections) {
      const headingMatch = section.match(/^<h[23] id="([^"]+)">/)
      const heading = headingMatch
        ? page.headings.find((candidate) => candidate.id === headingMatch[1])
        : undefined
      const route = heading ? `${page.route}#${heading.id}` : page.route
      const paragraphs = htmlToText(section).split(/(?<=[.!?])\s+(?=[A-Z0-9])/)
      let current = ''
      const flush = (): void => {
        const text = current.trim()
        if (!text) return
        chunks.push({
          id: `${routeArtifactId(page.route)}:${index++}`,
          route,
          title: page.title,
          ...(heading ? { heading: heading.text } : {}),
          text,
        })
        current = ''
      }
      for (const paragraph of paragraphs) {
        if (current.length + paragraph.length > 1_800) flush()
        current += `${current ? ' ' : ''}${paragraph}`
      }
      flush()
    }
    return chunks
  })
}

export function pagesInNavigationOrder(
  pages: DocumentationPage[],
  navigation: DocumentationNavigationNode[],
): DocumentationPage[] {
  const byRoute = new Map(pages.map((page) => [page.route, page]))
  const ordered: DocumentationPage[] = []
  const seen = new Set<string>()
  const visit = (nodes: DocumentationNavigationNode[]): void => {
    for (const node of nodes) {
      if (node.kind === 'group') {
        visit(node.items)
        continue
      }
      if (node.kind !== 'page' || seen.has(node.route)) continue
      const page = byRoute.get(node.route)
      if (page) ordered.push(page)
      seen.add(node.route)
    }
  }
  visit(navigation)
  for (const page of pages) {
    if (!seen.has(page.route)) ordered.push(page)
  }
  return ordered
}

export function renderLlmsIndex(pages: DocumentationPage[], name: string, basePath = '/docs'): string {
  const lines = [`# ${name}`, '', '> Documentation index generated by DeepSpace Documentation.', '']
  for (const page of pages.filter((candidate) => !candidate.hidden)) {
    lines.push(
      `- [${page.title}](${markdownUrlForRoute(page.route, basePath)})` +
        `${page.description ? `: ${page.description}` : ''}`,
    )
  }
  return `${lines.join('\n')}\n`
}

export function renderLlmsFull(
  pages: DocumentationPage[],
  name: string,
  basePath = '/docs',
): string {
  return `# ${name}\n\n${pages
    .filter((page) => !page.hidden)
    .map((page) =>
      `${pageMarkdown(page, basePath).trim()}\n\nSource: ${markdownUrlForRoute(page.route, basePath)}`,
    )
    .join('\n\n---\n\n')}\n`
}

export function renderSitemap(pages: DocumentationPage[], baseUrl: string | undefined): string {
  const urls = baseUrl
    ? pages
        .filter((page) => !page.hidden && !page.noindex)
        .map((page) => `  <url><loc>${escapeXml(joinSiteUrl(baseUrl, page.route))}</loc></url>`)
        .join('\n')
    : ''
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}${urls ? '\n' : ''}</urlset>\n`
}

export function renderRobots(baseUrl: string | undefined, noindex: boolean | undefined): string {
  if (noindex) return 'User-agent: *\nDisallow: /\n'
  return `User-agent: *\nAllow: /\n${baseUrl ? `Sitemap: ${joinSiteUrl(baseUrl, '/sitemap.xml')}\n` : ''}`
}

export function stableJson(value: unknown): string {
  return `${JSON.stringify(value, null, 2)}\n`
}
